import {
  type InfiniteData,
  type QueryFunctionContext,
  type UseInfiniteQueryResult,
  useInfiniteQuery,
} from '@tanstack/react-query';

import { memberService } from '@/services/member-service';
import type { Invitation } from '@/types/core/member';
import type { Page } from '@/types/shared/page';
import type { UUID } from '@/utils/uuid';

import { memberQueryKeys } from './query-keys';

/**
 * List invitations for a given tenant.
 * Accepts optional paging args via `opts`.
 */
export function useListInvitations(
  tenantId: UUID,
  opts?: {
    limit?: number;
    next?: string;
    filter?: string;
  }
): UseInfiniteQueryResult<InfiniteData<Page<Invitation>, unknown>, Error> {
  return useInfiniteQuery<
    Page<Invitation>,
    Error,
    InfiniteData<Page<Invitation>, unknown>
  >({
    queryKey: memberQueryKeys.invitationList(
      tenantId,
      opts?.limit,
      opts?.filter
    ),
    queryFn: (context: QueryFunctionContext) => {
      const pageParam = context.pageParam as string | undefined;
      return memberService.listInvitations(tenantId, {
        limit: opts?.limit,
        next: pageParam,
        filter: opts?.filter,
      });
    },
    initialPageParam: opts?.next ?? undefined,
    getNextPageParam: (last) => last.next ?? undefined,
    getPreviousPageParam: (first) => first.prev ?? undefined,
  });
}
